// Strings for release 2 (0018): head insight view, welfare report, student
// council, playbook and action log, counsellor requests, compliance pack,
// feature switches and school options. Merged into STRINGS in strings.ts.
// Finnish and Swedish are drafts and need native-speaker review.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };
const n = (c: number, one: string, many: string) => (c === 1 ? one : many);

export const r2En = {
  'ci.schoolSaid': 'From your school',
  'ci.draftTranslation': 'Draft translation',

  // head navigation
  'h2.navReport': 'Welfare report',
  'h2.navCouncil': 'Student council',
  'h2.navPlaybook': 'Playbook',
  'h2.navCompliance': 'Documents',
  // insight view
  'h2.scopeWhole': 'You see the whole school by grade.',
  'h2.scopeGrades': (g: string) => `You see grades ${g} only.`,
  'h2.neverClass': 'Results are never shown for a single class.',
  'h2.lastWeek': 'Last complete week',
  'h2.notShown': 'Not shown — too few or too uneven',
  'h2.scopedNoTotal': 'No school total for a grade-scoped view',
  'h2.answersLastWeek': 'Answers last week',
  'h2.inactive': 'Classes with no check-in for 14 days',
  'h2.inactiveSub': 'Count only — your school admin sees which',
  'h2.replies': '“You said, we did” replies',
  'h2.last4': 'Last four complete weeks',
  'h2.weeksTitle': 'Weekly results',
  'h2.weeksHint': 'Each week (Monday–Sunday) is shown on its own once it has ended. A week or grade with too few answers is hidden, and grades are hidden together when one alone would show through the total.',
  'h2.week': 'Week',
  'h2.school': 'School',
  'h2.grade': (g: string) => `Grade ${g}`,
  'h2.cellNote': 'Average mood out of 5 and the number of answers.',
  'h2.n': (c: number) => n(c, '1 answer', `${c} answers`),
  'h2.noAnswers': 'No answers',
  'h2.st.insufficient': 'Too few to show',
  'h2.st.insufficient_spread': 'Hidden — one class would show through',
  'h2.st.suppressed_near_unanimous': 'Hidden — nearly all the same',
  'h2.st.hidden_complement': 'Hidden with another grade',
  'h2.lastWeekMood': 'How students were doing last week',
  'h2.lastWeekTopics': 'On their minds last week',
  'h2.windowHidden': 'Last week is hidden: too few answers, or one group would show through.',
  'h2.partTitle': 'Participation by grade',
  'h2.partHint': (from: string, to: string) => `${from}–${to}, check-ins that have ended.`,
  'h2.partLine': (s: number, r: number) => `${n(s, '1 check-in', `${s} check-ins`)} · ${n(r, '1 answer', `${r} answers`)}`,
  'h2.rate': (p: number) => `${p}% of expected`,
  'h2.ctxTitle': 'What was going on and what was tried',
  'h2.ctxHint': 'How often teachers tagged a week and logged an action, school-wide. Never by class or teacher.',
  'h2.times': (c: number) => n(c, 'once', `${c} times`),
  'h2.nothing': 'Nothing recorded yet',
  'h2.footer': 'Viewing results is logged in the school’s Activity (who and when — never the numbers).',
  // welfare report
  'h2.reportTitle': 'Communal welfare report',
  'h2.reportSetup': 'Build the report',
  'h2.reportIntro': 'Evidence for the school’s student welfare plan and its yearly follow-up (Student Welfare Act 1287/2013). Built from complete weeks only. Add your own commentary, then print to PDF or download a Word file.',
  'h2.period': 'Period',
  'h2.period.autumn': 'Autumn term',
  'h2.period.spring': 'Spring term',
  'h2.period.year': 'School year',
  'h2.period.custom': 'Custom (at least 4 weeks)',
  'h2.from': 'From',
  'h2.to': 'To',
  'h2.reportLang': 'Report language',
  'h2.reportGrades': 'Grades',
  'h2.reportGradesHint': 'None ticked = the whole school.',
  'h2.preview': 'Preview report',
  'h2.exportPdf': 'Print or save as PDF',
  'h2.exportWord': 'Download Word file',
  'h2.exportHint': 'Each export is recorded in the school’s Activity and archive (who, when, period — never the numbers).',
  'h2.reportReady': 'Report ready.',
  'h2.exportLogged': 'Export recorded.',
  'h2.commentLabel': 'Your commentary for this section (optional)',
  'h2.previewOnly': 'Preview — only what you export leaves the app',
  'h2.reportDocTitle': 'Communal welfare report',
  'h2.wholeSchool': 'Whole school',
  'h2.gradesLabel': (g: string) => `Grades ${g}`,
  'h2.selected': 'Selected grades',
  'h2.reportBasis': 'Anonymous check-ins, complete weeks only. A week or grade with fewer than 5 answers, or too uneven to show safely, is hidden. No class or teacher is named.',
  'h2.headComment': 'Head of school’s commentary',
  'h2.secParticipation': 'Participation',
  'h2.secWellbeing': 'Wellbeing of the school community',
  'h2.secContext': 'Context',
  'h2.secActions': 'What the school did',
  'h2.secNotes': 'Data notes',
  'h2.summaryLine': (shown: number, all: number, r: number, mean: string) =>
    `Across the ${shown} of ${all} weeks that could be shown: ${r} answers, average mood ${mean}/5.`,
  'h2.summaryNone': 'No week in this period had enough answers to show.',
  'h2.repliesLine': (c: number) => `Teachers replied to their classes (“You said, we did”) ${n(c, 'once', `${c} times`)}.`,
  'h2.notesLine': (c: number) => `${n(c, '1 check-in', `${c} check-ins`)} had fewer than 5 answers; those answers count only in the totals above.`,
  'h2.never': 'Never in this report: a class’s results, a teacher’s name, or anything a teacher records privately.',
  // council
  'h2.councilTitle': 'Student council',
  'h2.councilIntro': 'Share school-level results with the student council through a link that ends by itself. It shows complete weeks for the whole school only, and only weeks with at least 10 answers. No grade, class or teacher.',
  'h2.councilScoped': 'Only a head of school who sees the whole school can share results with the student council.',
  'h2.linkNew': 'New link for the council',
  'h2.linkWeeks': 'Weeks to show',
  'h2.linkDays': 'Link works for',
  'h2.nWeeks': (w: number) => n(w, '1 week', `${w} weeks`),
  'h2.nDays': (d: number) => n(d, '1 day', `${d} days`),
  'h2.linkHint': 'Anyone with the link can open it, so share it with the council only. You can end it at any time.',
  'h2.linkCreate': '+ Create link',
  'h2.linkList': 'Links',
  'h2.linkNone': 'No links yet.',
  'h2.until': (d: string) => `until ${d}`,
  'h2.ended': 'Ended',
  'h2.views': (v: number) => n(v, 'opened once', `opened ${v} times`),
  'h2.revoke': 'End link',
  'h2.linkRevoked': 'Link ended.',
  'h2.linkCreatedTitle': 'Link for the student council',
  'h2.linkCreatedIntro': 'Copy it now: it is shown only once. It ends by itself on the date you chose.',
  'h2.copy': 'Copy link',
  'h2.copied': 'Copied.',
  'h2.done': 'Done',
  'h2.schoolSaid': 'You said, we did — to the whole school',
  'h2.schoolSaidHint': 'One line every student sees on their next check-in, for up to 30 days (a class’s own reply comes first). Use it to answer the council’s proposals.',
  'h2.schoolSaidLabel': 'One line to every student',
  'h2.schoolSaidPost': 'Post to the school',
  // playbook (head)
  'h2.playbookTitle': 'Playbook',
  'h2.pbCustomOn': 'Your school can adapt the playbook. Teachers propose changes; the primary head of school (or the approval delegate) approves them.',
  'h2.pbCustomOff': 'Your school uses Zencera’s standard playbook. Adapting it to your school is a premium option — ask Zencera.',
  'h2.pbProposals': 'Proposals waiting',
  'h2.pbNoProposals': 'No proposals waiting.',
  'h2.pbBy': (who: string) => `Proposed by ${who}`,
  'h2.pbPrimaryDecides': 'The primary head of school or the approval delegate decides.',
  'h2.pbApproved': 'Approved. Teachers now see your school’s version.',
  'h2.pbRejected': 'Rejected.',
  'h2.approve': 'Approve',
  'h2.reject': 'Reject',
  'h2.pbItems': 'Activities',
  'h2.pbSchoolVersion': 'Your school’s version',
  'h2.pbAnyTopic': 'Any topic',
  'h2.complianceTitle': 'Documents',

  // teacher: acting on results
  'act.title': 'What you could try',
  'act.needResults': 'Suggestions appear when this check-in’s results are shown.',
  'act.mainTopics': (t: string) => `Main topics: ${t}.`,
  'act.didThis': 'I did this',
  'act.orOther': 'Or log something else',
  'act.playbook': 'Playbook activity',
  'act.class_discussion': 'Class discussion',
  'act.adjusted_workload': 'Adjusted the workload',
  'act.counsellor_visit': 'Counsellor visit requested',
  'act.other': 'Something else',
  'act.logged': 'Logged.',
  'act.done': 'What you did',
  'act.histDid': (a: string) => `Did: ${a}`,
  'act.who': 'Only you see what you logged for this class. Your head of school sees how often each kind of action is used, school-wide.',
  'act.allPlaybook': 'See the whole playbook',
  'act.counsellorTitle': 'Ask the counsellor to visit',
  'act.ask': 'Ask the counsellor to visit the class',
  'act.askTitle': 'Ask the counsellor to visit',
  'act.askIntro': 'The counsellor sees the class and the topics you pick — no results, no names, nothing about any one student.',
  'act.askTopics': 'Topics to mention',
  'act.askSend': 'Send request',
  'act.asked': 'Request sent to the counsellor.',
  'act.reqStatus': (s: string) => `Counsellor request: ${s}.`,
  // teacher playbook
  'pb.customOn': 'You can propose your school’s own version of an activity, or a new one. The primary head of school approves it.',
  'pb.customOff': 'These are Zencera’s standard activities. Activities marked Draft haven’t been reviewed by a student-welfare professional yet.',
  'pb.proposeTitle': 'Propose an activity',
  'pb.base': 'Based on',
  'pb.newItem': 'A new activity',
  'pb.topic': 'Topic',
  'pb.titleLabel': 'Title',
  'pb.bodyLabel': 'What to do',
  'pb.rule': 'Class-level only. No names, nothing about any one student.',
  'pb.send': 'Send for approval',
  'pb.proposed': 'Sent to the head of school for approval.',

  // counsellor
  'co.nav': 'Visit requests',
  'co.title': 'Class visit requests',
  'co.intro': 'Teachers ask you to visit a class and name the topics. You see no results and no names.',
  'co.none': 'No requests.',
  'co.visit': (c: string) => `Visit ${c}`,
  'co.topics': (t: string) => `Came up: ${t}`,
  'co.noTopics': 'No topics named.',
  'co.asked': (who: string, when: string) => `Asked by ${who} · ${when}`,
  'co.ack': 'Acknowledge',
  'co.done': 'Mark visited',
  'co.acknowledgedDone': 'Acknowledged. The teacher is told.',
  'co.doneDone': 'Marked visited. The teacher is told.',
  'co.st.open': 'Waiting',
  'co.st.acknowledged': 'Acknowledged',
  'co.st.done': 'Visited',

  // compliance pack
  'cp.intro': 'Swipe’s documents for your school. Documents marked Draft are being reviewed by a lawyer or data protection officer; Zencera records the version your school signs.',
  'cp.publicIntro': 'What Swipe does with data and how it keeps answers anonymous. Documents marked Draft are under review.',
  'cp.trustTitle': 'Privacy and security',
  'cp.staffLink': 'Staff sign in',
  'cp.blocked': 'Waiting on a decision',
  'cp.accepted': (v: number) => `Signed v${v}`,
  'cp.notAccepted': 'Not signed yet',
  'cp.acceptedBy': (who: string, on: string) => `Signed by ${who} on ${on}`,
  'cp.version': (v: number, on: string) => `Version ${v} · ${on}`,
  'cp.for': (a: string) => `For: ${a}`,
  'cp.public': 'Public',
  'cp.read': 'Read',
  'cp.hide': 'Hide',
  'cp.onlyEnglish': 'This document is in English only for now.',
  'cp.download': 'Download',
  'cp.adminIntro': 'Publish a new version when a document changes. Documents a school signs notify its school admins and primary head, and the earlier version stays in force until the new one is recorded as signed.',
  'cp.acceptedCount': (c: number) => n(c, 'signed by 1 school', `signed by ${c} schools`),
  'cp.bodyEn': 'Text (English, Markdown)',
  'cp.changeNote': 'What changed',
  'cp.stillDraft': 'Still a draft (not yet reviewed)',
  'cp.publish': (v: number) => `Publish version ${v}`,
  'cp.newVersion': 'New version…',
  'cp.published': 'Published.',

  // council view (public)
  'cv.title': 'School check-in results for the student council',
  'cv.notFound': 'This link isn’t valid any more. Ask the head of school for a new one.',
  'cv.intro': (f: number) => `Anonymous results for the whole school, by complete week. A week with fewer than ${f} answers is hidden.`,
  'cv.weeks': 'Weekly results',
  'cv.expires': (d: string) => `This link works until ${d}.`,

  // feature switches
  'fs.title': 'Feature switches',
  'fs.hint': 'Every Workflow I and J feature can be switched off for all schools at once. The database refuses a switched-off feature.',
  'fs.isOn': 'On',
  'fs.isOff': 'Off',
  'fs.on': (f: string) => `${f} switched on.`,
  'fs.off': (f: string) => `${f} switched off.`,
  'fs.teacher_session_flow': 'Teacher session extras (forecast, extend)',
  'fs.teacher_predictions': 'Teacher predictions',
  'fs.private_notes': 'Private notes',
  'fs.you_said_we_did': 'You said, we did',
  'fs.session_schedules': 'Recurring check-ins',
  'fs.head_insights': 'Head of school insight view',
  'fs.welfare_report': 'Communal welfare report',
  'fs.compliance_pack': 'Compliance pack',
  'fs.playbook': 'Activity playbook',
  'fs.action_log': 'Action log',
  'fs.counsellor_requests': 'Counsellor visit requests',
  'fs.student_council': 'Student council link',
  'fs.extra_languages': 'Extra student languages',
  'fs.swipe_checkin': 'Swipe check-in (card deck, help strip, “Something else” phrases)',

  // school options (platform admin, support mode)
  'so.premium': 'Options and limits',
  'so.premiumHint': 'Premium options are billed separately. Changes are logged and archived.',
  'so.ent.custom_playbook': 'Custom playbook (premium)',
  'so.ent.insights_premium': 'Premium insights (premium)',
  'so.sessionMax': 'Longest check-in (minutes)',
  'so.langs': 'Extra student languages',
  'so.langsHint': 'Students can choose these on the check-in. The translations are drafts. The school writes its own safety-net message in each language.',
  'so.netIn': (l: string) => `Safety-net message in ${l}`,
  'so.netFallback': 'Without a message in a language, students see the school’s Finnish and English messages.',
  'so.docs': 'Documents the school signs',
  'so.docsHint': 'Record a signature from the signed copy. It goes to the critical-records archive.',
  'so.record': 'Record signature',
  'so.signedOn': 'Signed on',
  'so.signatory': 'Signed by',

  'err.bad_period': 'Choose a term, the school year, or at least 4 weeks.',
  'err.not_entitled': 'Your school hasn’t enabled this premium option.',
  'err.unknown_request': 'This is no longer waiting. Reload the page.',
};

export type R2Strings = Shape<typeof r2En>;

export const r2Fi: R2Strings = {
  'ci.schoolSaid': 'Koulultasi',
  'ci.draftTranslation': 'Käännösluonnos',
  'h2.navReport': 'Hyvinvointiraportti',
  'h2.navCouncil': 'Oppilaskunta',
  'h2.navPlaybook': 'Toimintamalleja',
  'h2.navCompliance': 'Asiakirjat',
  'h2.scopeWhole': 'Näet koko koulun luokka-asteittain.',
  'h2.scopeGrades': (g) => `Näet vain luokka-asteet ${g}.`,
  'h2.neverClass': 'Yksittäisen luokan tuloksia ei koskaan näytetä.',
  'h2.lastWeek': 'Viimeisin kokonainen viikko',
  'h2.notShown': 'Ei näytetä – liian vähän tai liian yksipuolista',
  'h2.scopedNoTotal': 'Ei koulun kokonaislukua luokka-asterajatussa näkymässä',
  'h2.answersLastWeek': 'Vastauksia viime viikolla',
  'h2.inactive': 'Luokat, joilla ei kyselyä 14 päivään',
  'h2.inactiveSub': 'Vain määrä – koulun ylläpitäjä näkee mitkä',
  'h2.replies': '”Te sanoitte, me teimme” -vastaukset',
  'h2.last4': 'Neljä viimeisintä kokonaista viikkoa',
  'h2.weeksTitle': 'Viikoittaiset tulokset',
  'h2.weeksHint': 'Jokainen viikko (ma–su) näytetään erikseen, kun se on päättynyt. Viikko tai luokka-aste, jolla on liian vähän vastauksia, piilotetaan, ja luokka-asteet piilotetaan yhdessä, jos yksi näkyisi kokonaisluvun kautta.',
  'h2.week': 'Viikko',
  'h2.school': 'Koulu',
  'h2.grade': (g) => `${g}. lk`,
  'h2.cellNote': 'Mielialan keskiarvo (1–5) ja vastausten määrä.',
  'h2.n': (c) => n(c, '1 vastaus', `${c} vastausta`),
  'h2.noAnswers': 'Ei vastauksia',
  'h2.st.insufficient': 'Liian vähän näytettäväksi',
  'h2.st.insufficient_spread': 'Piilotettu – yksi luokka näkyisi',
  'h2.st.suppressed_near_unanimous': 'Piilotettu – lähes kaikki samaa',
  'h2.st.hidden_complement': 'Piilotettu toisen luokka-asteen kanssa',
  'h2.lastWeekMood': 'Miten oppilailla meni viime viikolla',
  'h2.lastWeekTopics': 'Mielessä viime viikolla',
  'h2.windowHidden': 'Viime viikko on piilotettu: liian vähän vastauksia tai jokin ryhmä näkyisi.',
  'h2.partTitle': 'Osallistuminen luokka-asteittain',
  'h2.partHint': (from, to) => `${from}–${to}, päättyneet kyselyt.`,
  'h2.partLine': (s, r) => `${n(s, '1 kysely', `${s} kyselyä`)} · ${n(r, '1 vastaus', `${r} vastausta`)}`,
  'h2.rate': (p) => `${p} % odotetusta`,
  'h2.ctxTitle': 'Mitä tapahtui ja mitä kokeiltiin',
  'h2.ctxHint': 'Kuinka usein opettajat merkitsivät viikon ja kirjasivat toimen koko koulussa. Ei koskaan luokittain tai opettajittain.',
  'h2.times': (c) => n(c, 'kerran', `${c} kertaa`),
  'h2.nothing': 'Ei vielä kirjauksia',
  'h2.footer': 'Tulosten katselu kirjataan koulun tapahtumalokiin (kuka ja milloin – ei koskaan lukuja).',
  'h2.reportTitle': 'Yhteisöllisen opiskeluhuollon raportti',
  'h2.reportSetup': 'Kokoa raportti',
  'h2.reportIntro': 'Näyttöä koulun opiskeluhuoltosuunnitelmaan ja sen vuosittaiseen seurantaan (oppilas- ja opiskelijahuoltolaki 1287/2013). Koostetaan vain kokonaisista viikoista. Lisää omat kommenttisi ja tulosta PDF:ksi tai lataa Word-tiedosto.',
  'h2.period': 'Ajanjakso',
  'h2.period.autumn': 'Syyslukukausi',
  'h2.period.spring': 'Kevätlukukausi',
  'h2.period.year': 'Lukuvuosi',
  'h2.period.custom': 'Oma (vähintään 4 viikkoa)',
  'h2.from': 'Alkaen',
  'h2.to': 'Päättyen',
  'h2.reportLang': 'Raportin kieli',
  'h2.reportGrades': 'Luokka-asteet',
  'h2.reportGradesHint': 'Ei valintoja = koko koulu.',
  'h2.preview': 'Esikatsele raporttia',
  'h2.exportPdf': 'Tulosta tai tallenna PDF:ksi',
  'h2.exportWord': 'Lataa Word-tiedosto',
  'h2.exportHint': 'Jokainen vienti kirjataan koulun tapahtumalokiin ja arkistoon (kuka, milloin, ajanjakso – ei koskaan lukuja).',
  'h2.reportReady': 'Raportti valmis.',
  'h2.exportLogged': 'Vienti kirjattu.',
  'h2.commentLabel': 'Kommenttisi tähän osioon (valinnainen)',
  'h2.previewOnly': 'Esikatselu – vain viemäsi raportti lähtee sovelluksesta',
  'h2.reportDocTitle': 'Yhteisöllisen opiskeluhuollon raportti',
  'h2.wholeSchool': 'Koko koulu',
  'h2.gradesLabel': (g) => `Luokka-asteet ${g}`,
  'h2.selected': 'Valitut luokka-asteet',
  'h2.reportBasis': 'Nimettömät kyselyt, vain kokonaiset viikot. Viikko tai luokka-aste, jolla on alle 5 vastausta tai joka on liian yksipuolinen näytettäväksi turvallisesti, piilotetaan. Luokkia tai opettajia ei nimetä.',
  'h2.headComment': 'Rehtorin kommentti',
  'h2.secParticipation': 'Osallistuminen',
  'h2.secWellbeing': 'Kouluyhteisön hyvinvointi',
  'h2.secContext': 'Tilanne',
  'h2.secActions': 'Mitä koulu teki',
  'h2.secNotes': 'Huomioita aineistosta',
  'h2.summaryLine': (shown, all, r, mean) => `Niiltä ${shown}/${all} viikolta, jotka voitiin näyttää: ${r} vastausta, mielialan keskiarvo ${mean}/5.`,
  'h2.summaryNone': 'Millään tämän jakson viikolla ei ollut tarpeeksi vastauksia näytettäväksi.',
  'h2.repliesLine': (c) => `Opettajat vastasivat luokilleen (”Te sanoitte, me teimme”) ${n(c, 'kerran', `${c} kertaa`)}.`,
  'h2.notesLine': (c) => `${n(c, '1 kyselyssä', `${c} kyselyssä`)} oli alle 5 vastausta; ne lasketaan vain yllä oleviin kokonaismääriin.`,
  'h2.never': 'Tässä raportissa ei koskaan ole: luokan tuloksia, opettajan nimeä tai mitään, mitä opettaja kirjaa itselleen.',
  'h2.councilTitle': 'Oppilaskunta',
  'h2.councilIntro': 'Jaa koulutason tulokset oppilaskunnalle linkillä, joka vanhenee itsestään. Se näyttää vain koko koulun kokonaiset viikot, joilla on vähintään 10 vastausta. Ei luokka-asteita, luokkia tai opettajia.',
  'h2.councilScoped': 'Vain koko koulun näkevä rehtori voi jakaa tuloksia oppilaskunnalle.',
  'h2.linkNew': 'Uusi linkki oppilaskunnalle',
  'h2.linkWeeks': 'Näytettävät viikot',
  'h2.linkDays': 'Linkki toimii',
  'h2.nWeeks': (w) => n(w, '1 viikko', `${w} viikkoa`),
  'h2.nDays': (d) => n(d, '1 päivän', `${d} päivää`),
  'h2.linkHint': 'Kuka tahansa linkin saanut voi avata sen, joten jaa se vain oppilaskunnalle. Voit lopettaa sen milloin tahansa.',
  'h2.linkCreate': '+ Luo linkki',
  'h2.linkList': 'Linkit',
  'h2.linkNone': 'Ei vielä linkkejä.',
  'h2.until': (d) => `${d} asti`,
  'h2.ended': 'Päättynyt',
  'h2.views': (v) => n(v, 'avattu kerran', `avattu ${v} kertaa`),
  'h2.revoke': 'Lopeta linkki',
  'h2.linkRevoked': 'Linkki lopetettu.',
  'h2.linkCreatedTitle': 'Linkki oppilaskunnalle',
  'h2.linkCreatedIntro': 'Kopioi se nyt: se näytetään vain kerran. Se vanhenee itsestään valitsemanasi päivänä.',
  'h2.copy': 'Kopioi linkki',
  'h2.copied': 'Kopioitu.',
  'h2.done': 'Valmis',
  'h2.schoolSaid': 'Te sanoitte, me teimme – koko koululle',
  'h2.schoolSaidHint': 'Yksi rivi, jonka jokainen oppilas näkee seuraavassa kyselyssään enintään 30 päivän ajan (luokan oma vastaus näytetään ensin). Vastaa sillä oppilaskunnan ehdotuksiin.',
  'h2.schoolSaidLabel': 'Yksi rivi kaikille oppilaille',
  'h2.schoolSaidPost': 'Julkaise koululle',
  'h2.playbookTitle': 'Toimintamallit',
  'h2.pbCustomOn': 'Koulusi voi muokata toimintamalleja. Opettajat ehdottavat muutoksia; ensisijainen rehtori (tai hyväksyntäsijainen) hyväksyy ne.',
  'h2.pbCustomOff': 'Koulusi käyttää Zenceran vakiotoimintamalleja. Niiden muokkaaminen koulullesi on lisämaksullinen vaihtoehto – kysy Zencerasta.',
  'h2.pbProposals': 'Odottavat ehdotukset',
  'h2.pbNoProposals': 'Ei odottavia ehdotuksia.',
  'h2.pbBy': (who) => `Ehdottaja: ${who}`,
  'h2.pbPrimaryDecides': 'Ensisijainen rehtori tai hyväksyntäsijainen päättää.',
  'h2.pbApproved': 'Hyväksytty. Opettajat näkevät nyt koulusi version.',
  'h2.pbRejected': 'Hylätty.',
  'h2.approve': 'Hyväksy',
  'h2.reject': 'Hylkää',
  'h2.pbItems': 'Toiminnot',
  'h2.pbSchoolVersion': 'Koulusi versio',
  'h2.pbAnyTopic': 'Mikä tahansa aihe',
  'h2.complianceTitle': 'Asiakirjat',
  'act.title': 'Mitä voisit kokeilla',
  'act.needResults': 'Ehdotukset näkyvät, kun tämän kyselyn tulokset näytetään.',
  'act.mainTopics': (t) => `Pääaiheet: ${t}.`,
  'act.didThis': 'Tein tämän',
  'act.orOther': 'Tai kirjaa jotain muuta',
  'act.playbook': 'Toimintamalli',
  'act.class_discussion': 'Luokkakeskustelu',
  'act.adjusted_workload': 'Kevensin työmäärää',
  'act.counsellor_visit': 'Kuraattorin vierailua pyydetty',
  'act.other': 'Jotain muuta',
  'act.logged': 'Kirjattu.',
  'act.done': 'Mitä teit',
  'act.histDid': (a) => `Tehty: ${a}`,
  'act.who': 'Vain sinä näet, mitä kirjasit tälle luokalle. Rehtori näkee, kuinka usein kutakin toimea käytetään koko koulussa.',
  'act.allPlaybook': 'Näytä kaikki toimintamallit',
  'act.counsellorTitle': 'Pyydä kuraattoria vierailemaan',
  'act.ask': 'Pyydä kuraattoria vierailemaan luokassa',
  'act.askTitle': 'Pyydä kuraattoria vierailemaan',
  'act.askIntro': 'Kuraattori näkee luokan ja valitsemasi aiheet – ei tuloksia, ei nimiä, ei mitään yksittäisestä oppilaasta.',
  'act.askTopics': 'Mainittavat aiheet',
  'act.askSend': 'Lähetä pyyntö',
  'act.asked': 'Pyyntö lähetetty kuraattorille.',
  'act.reqStatus': (s) => `Kuraattoripyyntö: ${s}.`,
  'pb.customOn': 'Voit ehdottaa koulusi omaa versiota toiminnosta tai uutta toimintoa. Ensisijainen rehtori hyväksyy sen.',
  'pb.customOff': 'Nämä ovat Zenceran vakiotoimintoja. Luonnokseksi merkittyjä ei ole vielä tarkistanut opiskeluhuollon ammattilainen.',
  'pb.proposeTitle': 'Ehdota toimintoa',
  'pb.base': 'Pohjana',
  'pb.newItem': 'Uusi toiminto',
  'pb.topic': 'Aihe',
  'pb.titleLabel': 'Otsikko',
  'pb.bodyLabel': 'Mitä tehdään',
  'pb.rule': 'Vain luokkatasolla. Ei nimiä eikä mitään yksittäisestä oppilaasta.',
  'pb.send': 'Lähetä hyväksyttäväksi',
  'pb.proposed': 'Lähetetty rehtorille hyväksyttäväksi.',
  'co.nav': 'Vierailupyynnöt',
  'co.title': 'Luokkien vierailupyynnöt',
  'co.intro': 'Opettajat pyytävät sinua vierailemaan luokassa ja nimeävät aiheet. Et näe tuloksia etkä nimiä.',
  'co.none': 'Ei pyyntöjä.',
  'co.visit': (c) => `Vierailu: ${c}`,
  'co.topics': (t) => `Esille tuli: ${t}`,
  'co.noTopics': 'Aiheita ei nimetty.',
  'co.asked': (who, when) => `Pyytäjä ${who} · ${when}`,
  'co.ack': 'Kuittaa',
  'co.done': 'Merkitse käydyksi',
  'co.acknowledgedDone': 'Kuitattu. Opettajalle ilmoitetaan.',
  'co.doneDone': 'Merkitty käydyksi. Opettajalle ilmoitetaan.',
  'co.st.open': 'Odottaa',
  'co.st.acknowledged': 'Kuitattu',
  'co.st.done': 'Käyty',
  'cp.intro': 'Swipen asiakirjat koulullesi. Luonnokseksi merkityt ovat juristin tai tietosuojavastaavan tarkastettavana; Zencera kirjaa koulusi allekirjoittaman version.',
  'cp.publicIntro': 'Mitä Swipe tekee tiedoilla ja miten se pitää vastaukset nimettöminä. Luonnokseksi merkityt ovat tarkastettavana.',
  'cp.trustTitle': 'Tietosuoja ja tietoturva',
  'cp.staffLink': 'Henkilökunnan kirjautuminen',
  'cp.blocked': 'Odottaa päätöstä',
  'cp.accepted': (v) => `Allekirjoitettu v${v}`,
  'cp.notAccepted': 'Ei vielä allekirjoitettu',
  'cp.acceptedBy': (who, on) => `Allekirjoittaja ${who}, ${on}`,
  'cp.version': (v, on) => `Versio ${v} · ${on}`,
  'cp.for': (a) => `Kenelle: ${a}`,
  'cp.public': 'Julkinen',
  'cp.read': 'Lue',
  'cp.hide': 'Piilota',
  'cp.onlyEnglish': 'Tämä asiakirja on toistaiseksi vain englanniksi.',
  'cp.download': 'Lataa',
  'cp.adminIntro': 'Julkaise uusi versio, kun asiakirja muuttuu. Koulun allekirjoittamista asiakirjoista ilmoitetaan koulun ylläpitäjille ja ensisijaiselle rehtorille, ja aiempi versio on voimassa, kunnes uusi kirjataan allekirjoitetuksi.',
  'cp.acceptedCount': (c) => n(c, 'allekirjoittanut 1 koulu', `allekirjoittanut ${c} koulua`),
  'cp.bodyEn': 'Teksti (englanniksi, Markdown)',
  'cp.changeNote': 'Mitä muuttui',
  'cp.stillDraft': 'Edelleen luonnos (ei tarkastettu)',
  'cp.publish': (v) => `Julkaise versio ${v}`,
  'cp.newVersion': 'Uusi versio…',
  'cp.published': 'Julkaistu.',
  'cv.title': 'Koulun kyselytulokset oppilaskunnalle',
  'cv.notFound': 'Tämä linkki ei ole enää voimassa. Pyydä rehtorilta uusi.',
  'cv.intro': (f) => `Nimettömät tulokset koko koulusta kokonaisina viikkoina. Viikko, jolla on alle ${f} vastausta, piilotetaan.`,
  'cv.weeks': 'Viikoittaiset tulokset',
  'cv.expires': (d) => `Linkki toimii ${d} asti.`,
  'fs.title': 'Toimintojen kytkimet',
  'fs.hint': 'Jokaisen työnkulkujen I ja J toiminnon voi kytkeä pois kaikilta kouluilta kerralla. Tietokanta torjuu pois kytketyn toiminnon.',
  'fs.isOn': 'Päällä',
  'fs.isOff': 'Pois',
  'fs.on': (f) => `${f} kytketty päälle.`,
  'fs.off': (f) => `${f} kytketty pois.`,
  'fs.teacher_session_flow': 'Opettajan kyselyn lisät (ennuste, jatkaminen)',
  'fs.teacher_predictions': 'Opettajan ennusteet',
  'fs.private_notes': 'Omat muistiinpanot',
  'fs.you_said_we_did': 'Te sanoitte, me teimme',
  'fs.session_schedules': 'Toistuvat kyselyt',
  'fs.head_insights': 'Rehtorin tulosnäkymä',
  'fs.welfare_report': 'Opiskeluhuollon raportti',
  'fs.compliance_pack': 'Asiakirjapaketti',
  'fs.playbook': 'Toimintamallit',
  'fs.action_log': 'Toimien kirjaus',
  'fs.counsellor_requests': 'Kuraattorin vierailupyynnöt',
  'fs.student_council': 'Oppilaskunnan linkki',
  'fs.extra_languages': 'Lisäkielet oppilaille',
  'fs.swipe_checkin': 'Korttipino-kysely (korttipino, tukipalkki, ”Jokin muu” -vaihtoehdot)',
  'so.premium': 'Valinnat ja rajat',
  'so.premiumHint': 'Lisämaksulliset vaihtoehdot laskutetaan erikseen. Muutokset kirjataan ja arkistoidaan.',
  'so.ent.custom_playbook': 'Oma toimintamallisto (lisämaksu)',
  'so.ent.insights_premium': 'Laajemmat tulokset (lisämaksu)',
  'so.sessionMax': 'Pisin kysely (minuuttia)',
  'so.langs': 'Lisäkielet oppilaille',
  'so.langsHint': 'Oppilaat voivat valita nämä kyselyssä. Käännökset ovat luonnoksia. Koulu kirjoittaa oman tukiviestinsä kullakin kielellä.',
  'so.netIn': (l) => `Tukiviesti kielellä ${l}`,
  'so.netFallback': 'Jos kielellä ei ole viestiä, oppilaat näkevät koulun suomen- ja englanninkieliset viestit.',
  'so.docs': 'Koulun allekirjoittamat asiakirjat',
  'so.docsHint': 'Kirjaa allekirjoitus allekirjoitetusta kappaleesta. Se tallentuu arkistoon.',
  'so.record': 'Kirjaa allekirjoitus',
  'so.signedOn': 'Allekirjoitettu',
  'so.signatory': 'Allekirjoittaja',
  'err.bad_period': 'Valitse lukukausi, lukuvuosi tai vähintään 4 viikkoa.',
  'err.not_entitled': 'Koulusi ei ole ottanut tätä lisämaksullista vaihtoehtoa käyttöön.',
  'err.unknown_request': 'Tämä ei enää odota. Lataa sivu uudelleen.',
};

export const r2Sv: R2Strings = {
  'ci.schoolSaid': 'Från din skola',
  'ci.draftTranslation': 'Översättningsutkast',
  'h2.navReport': 'Välfärdsrapport',
  'h2.navCouncil': 'Elevkåren',
  'h2.navPlaybook': 'Åtgärdsbank',
  'h2.navCompliance': 'Dokument',
  'h2.scopeWhole': 'Du ser hela skolan per årskurs.',
  'h2.scopeGrades': (g) => `Du ser bara årskurserna ${g}.`,
  'h2.neverClass': 'Resultat visas aldrig för en enskild klass.',
  'h2.lastWeek': 'Senaste hela vecka',
  'h2.notShown': 'Visas inte – för få eller för ensidigt',
  'h2.scopedNoTotal': 'Ingen totalsumma för skolan i en årskursbegränsad vy',
  'h2.answersLastWeek': 'Svar förra veckan',
  'h2.inactive': 'Klasser utan incheckning på 14 dagar',
  'h2.inactiveSub': 'Bara antal – skolans administratör ser vilka',
  'h2.replies': '”Ni sa, vi gjorde”-svar',
  'h2.last4': 'De fyra senaste hela veckorna',
  'h2.weeksTitle': 'Resultat per vecka',
  'h2.weeksHint': 'Varje vecka (mån–sön) visas för sig när den har avslutats. En vecka eller årskurs med för få svar döljs, och årskurser döljs tillsammans när en ensam skulle synas genom totalsumman.',
  'h2.week': 'Vecka',
  'h2.school': 'Skolan',
  'h2.grade': (g) => `Åk ${g}`,
  'h2.cellNote': 'Humörets medelvärde (1–5) och antalet svar.',
  'h2.n': (c) => n(c, '1 svar', `${c} svar`),
  'h2.noAnswers': 'Inga svar',
  'h2.st.insufficient': 'För få för att visas',
  'h2.st.insufficient_spread': 'Dold – en klass skulle synas',
  'h2.st.suppressed_near_unanimous': 'Dold – nästan alla lika',
  'h2.st.hidden_complement': 'Dold tillsammans med en annan årskurs',
  'h2.lastWeekMood': 'Hur eleverna mådde förra veckan',
  'h2.lastWeekTopics': 'Det de tänkte på förra veckan',
  'h2.windowHidden': 'Förra veckan är dold: för få svar, eller en grupp skulle synas.',
  'h2.partTitle': 'Deltagande per årskurs',
  'h2.partHint': (from, to) => `${from}–${to}, avslutade incheckningar.`,
  'h2.partLine': (s, r) => `${n(s, '1 incheckning', `${s} incheckningar`)} · ${n(r, '1 svar', `${r} svar`)}`,
  'h2.rate': (p) => `${p} % av förväntat`,
  'h2.ctxTitle': 'Vad som pågick och vad som prövades',
  'h2.ctxHint': 'Hur ofta lärare märkte en vecka och loggade en åtgärd i hela skolan. Aldrig per klass eller lärare.',
  'h2.times': (c) => n(c, 'en gång', `${c} gånger`),
  'h2.nothing': 'Inget registrerat ännu',
  'h2.footer': 'Att se resultat loggas i skolans händelselogg (vem och när – aldrig siffrorna).',
  'h2.reportTitle': 'Rapport om gemensam elevhälsa',
  'h2.reportSetup': 'Sammanställ rapporten',
  'h2.reportIntro': 'Underlag för skolans elevhälsoplan och dess årliga uppföljning (lagen om elev- och studerandevård 1287/2013). Sammanställs bara av hela veckor. Lägg till egna kommentarer och skriv ut som PDF eller ladda ner en Word-fil.',
  'h2.period': 'Period',
  'h2.period.autumn': 'Hösttermin',
  'h2.period.spring': 'Vårtermin',
  'h2.period.year': 'Läsår',
  'h2.period.custom': 'Egen (minst 4 veckor)',
  'h2.from': 'Från',
  'h2.to': 'Till',
  'h2.reportLang': 'Rapportens språk',
  'h2.reportGrades': 'Årskurser',
  'h2.reportGradesHint': 'Inget valt = hela skolan.',
  'h2.preview': 'Förhandsgranska rapporten',
  'h2.exportPdf': 'Skriv ut eller spara som PDF',
  'h2.exportWord': 'Ladda ner Word-fil',
  'h2.exportHint': 'Varje export registreras i skolans händelselogg och arkiv (vem, när, period – aldrig siffrorna).',
  'h2.reportReady': 'Rapporten är klar.',
  'h2.exportLogged': 'Exporten har registrerats.',
  'h2.commentLabel': 'Din kommentar till detta avsnitt (valfritt)',
  'h2.previewOnly': 'Förhandsgranskning – bara det du exporterar lämnar appen',
  'h2.reportDocTitle': 'Rapport om gemensam elevhälsa',
  'h2.wholeSchool': 'Hela skolan',
  'h2.gradesLabel': (g) => `Årskurserna ${g}`,
  'h2.selected': 'Valda årskurser',
  'h2.reportBasis': 'Anonyma incheckningar, bara hela veckor. En vecka eller årskurs med färre än 5 svar, eller för ensidig för att visas säkert, döljs. Ingen klass eller lärare nämns.',
  'h2.headComment': 'Rektorns kommentar',
  'h2.secParticipation': 'Deltagande',
  'h2.secWellbeing': 'Skolgemenskapens välmående',
  'h2.secContext': 'Sammanhang',
  'h2.secActions': 'Vad skolan gjorde',
  'h2.secNotes': 'Om underlaget',
  'h2.summaryLine': (shown, all, r, mean) => `Över de ${shown} av ${all} veckor som kunde visas: ${r} svar, humörets medelvärde ${mean}/5.`,
  'h2.summaryNone': 'Ingen vecka under perioden hade tillräckligt många svar för att visas.',
  'h2.repliesLine': (c) => `Lärare svarade sina klasser (”Ni sa, vi gjorde”) ${n(c, 'en gång', `${c} gånger`)}.`,
  'h2.notesLine': (c) => `${n(c, '1 incheckning', `${c} incheckningar`)} hade färre än 5 svar; de räknas bara in i totalsummorna ovan.`,
  'h2.never': 'Aldrig i rapporten: en klass resultat, en lärares namn eller något en lärare antecknar privat.',
  'h2.councilTitle': 'Elevkåren',
  'h2.councilIntro': 'Dela resultat på skolnivå med elevkåren via en länk som upphör av sig själv. Den visar bara hela veckor för hela skolan, och bara veckor med minst 10 svar. Ingen årskurs, klass eller lärare.',
  'h2.councilScoped': 'Bara en rektor som ser hela skolan kan dela resultat med elevkåren.',
  'h2.linkNew': 'Ny länk till elevkåren',
  'h2.linkWeeks': 'Veckor som visas',
  'h2.linkDays': 'Länken fungerar i',
  'h2.nWeeks': (w) => n(w, '1 vecka', `${w} veckor`),
  'h2.nDays': (d) => n(d, '1 dag', `${d} dagar`),
  'h2.linkHint': 'Vem som helst med länken kan öppna den, så dela den bara med elevkåren. Du kan avsluta den när som helst.',
  'h2.linkCreate': '+ Skapa länk',
  'h2.linkList': 'Länkar',
  'h2.linkNone': 'Inga länkar ännu.',
  'h2.until': (d) => `till ${d}`,
  'h2.ended': 'Avslutad',
  'h2.views': (v) => n(v, 'öppnad en gång', `öppnad ${v} gånger`),
  'h2.revoke': 'Avsluta länken',
  'h2.linkRevoked': 'Länken har avslutats.',
  'h2.linkCreatedTitle': 'Länk till elevkåren',
  'h2.linkCreatedIntro': 'Kopiera den nu: den visas bara en gång. Den upphör av sig själv det datum du valde.',
  'h2.copy': 'Kopiera länken',
  'h2.copied': 'Kopierad.',
  'h2.done': 'Klar',
  'h2.schoolSaid': 'Ni sa, vi gjorde – till hela skolan',
  'h2.schoolSaidHint': 'En rad som varje elev ser vid sin nästa incheckning i högst 30 dagar (klassens eget svar visas först). Använd den för att svara på elevkårens förslag.',
  'h2.schoolSaidLabel': 'En rad till alla elever',
  'h2.schoolSaidPost': 'Publicera för skolan',
  'h2.playbookTitle': 'Åtgärdsbank',
  'h2.pbCustomOn': 'Din skola kan anpassa åtgärdsbanken. Lärare föreslår ändringar; den primära rektorn (eller godkännandeombudet) godkänner dem.',
  'h2.pbCustomOff': 'Din skola använder Zenceras standardåtgärder. Att anpassa dem för skolan är ett tilläggsalternativ – fråga Zencera.',
  'h2.pbProposals': 'Väntande förslag',
  'h2.pbNoProposals': 'Inga väntande förslag.',
  'h2.pbBy': (who) => `Föreslagen av ${who}`,
  'h2.pbPrimaryDecides': 'Den primära rektorn eller godkännandeombudet beslutar.',
  'h2.pbApproved': 'Godkänt. Lärarna ser nu skolans version.',
  'h2.pbRejected': 'Avslaget.',
  'h2.approve': 'Godkänn',
  'h2.reject': 'Avslå',
  'h2.pbItems': 'Åtgärder',
  'h2.pbSchoolVersion': 'Skolans version',
  'h2.pbAnyTopic': 'Vilket ämne som helst',
  'h2.complianceTitle': 'Dokument',
  'act.title': 'Vad du kunde pröva',
  'act.needResults': 'Förslagen visas när den här incheckningens resultat visas.',
  'act.mainTopics': (t) => `Huvudämnen: ${t}.`,
  'act.didThis': 'Jag gjorde detta',
  'act.orOther': 'Eller logga något annat',
  'act.playbook': 'Åtgärd ur banken',
  'act.class_discussion': 'Klassamtal',
  'act.adjusted_workload': 'Lättade arbetsbördan',
  'act.counsellor_visit': 'Kuratorsbesök begärt',
  'act.other': 'Något annat',
  'act.logged': 'Loggat.',
  'act.done': 'Vad du gjorde',
  'act.histDid': (a) => `Gjort: ${a}`,
  'act.who': 'Bara du ser vad du loggade för den här klassen. Rektorn ser hur ofta varje typ av åtgärd används i hela skolan.',
  'act.allPlaybook': 'Visa hela åtgärdsbanken',
  'act.counsellorTitle': 'Be kuratorn besöka klassen',
  'act.ask': 'Be kuratorn besöka klassen',
  'act.askTitle': 'Be kuratorn besöka klassen',
  'act.askIntro': 'Kuratorn ser klassen och de ämnen du väljer – inga resultat, inga namn, inget om en enskild elev.',
  'act.askTopics': 'Ämnen att nämna',
  'act.askSend': 'Skicka begäran',
  'act.asked': 'Begäran har skickats till kuratorn.',
  'act.reqStatus': (s) => `Begäran till kuratorn: ${s}.`,
  'pb.customOn': 'Du kan föreslå skolans egen version av en åtgärd, eller en ny. Den primära rektorn godkänner den.',
  'pb.customOff': 'Detta är Zenceras standardåtgärder. De som är märkta som utkast har inte granskats av en elevhälsoexpert ännu.',
  'pb.proposeTitle': 'Föreslå en åtgärd',
  'pb.base': 'Baserad på',
  'pb.newItem': 'En ny åtgärd',
  'pb.topic': 'Ämne',
  'pb.titleLabel': 'Rubrik',
  'pb.bodyLabel': 'Vad som görs',
  'pb.rule': 'Bara på klassnivå. Inga namn, inget om en enskild elev.',
  'pb.send': 'Skicka för godkännande',
  'pb.proposed': 'Skickat till rektorn för godkännande.',
  'co.nav': 'Besöksbegäran',
  'co.title': 'Begäran om klassbesök',
  'co.intro': 'Lärare ber dig besöka en klass och nämner ämnena. Du ser inga resultat och inga namn.',
  'co.none': 'Inga begäran.',
  'co.visit': (c) => `Besök ${c}`,
  'co.topics': (t) => `Kom upp: ${t}`,
  'co.noTopics': 'Inga ämnen nämnda.',
  'co.asked': (who, when) => `Begärt av ${who} · ${when}`,
  'co.ack': 'Kvittera',
  'co.done': 'Markera som besökt',
  'co.acknowledgedDone': 'Kvitterat. Läraren meddelas.',
  'co.doneDone': 'Markerat som besökt. Läraren meddelas.',
  'co.st.open': 'Väntar',
  'co.st.acknowledged': 'Kvitterat',
  'co.st.done': 'Besökt',
  'cp.intro': 'Swipes dokument för din skola. De som är märkta som utkast granskas av en jurist eller dataskyddsombud; Zencera registrerar den version din skola undertecknar.',
  'cp.publicIntro': 'Vad Swipe gör med uppgifter och hur svaren hålls anonyma. De som är märkta som utkast granskas.',
  'cp.trustTitle': 'Dataskydd och informationssäkerhet',
  'cp.staffLink': 'Inloggning för personal',
  'cp.blocked': 'Väntar på beslut',
  'cp.accepted': (v) => `Undertecknat v${v}`,
  'cp.notAccepted': 'Inte undertecknat ännu',
  'cp.acceptedBy': (who, on) => `Undertecknat av ${who} den ${on}`,
  'cp.version': (v, on) => `Version ${v} · ${on}`,
  'cp.for': (a) => `För: ${a}`,
  'cp.public': 'Offentligt',
  'cp.read': 'Läs',
  'cp.hide': 'Dölj',
  'cp.onlyEnglish': 'Dokumentet finns tills vidare bara på engelska.',
  'cp.download': 'Ladda ner',
  'cp.adminIntro': 'Publicera en ny version när ett dokument ändras. För dokument som skolan undertecknar meddelas skolans administratörer och primära rektor, och den tidigare versionen gäller tills den nya registreras som undertecknad.',
  'cp.acceptedCount': (c) => n(c, 'undertecknat av 1 skola', `undertecknat av ${c} skolor`),
  'cp.bodyEn': 'Text (engelska, Markdown)',
  'cp.changeNote': 'Vad som ändrades',
  'cp.stillDraft': 'Fortfarande utkast (inte granskat)',
  'cp.publish': (v) => `Publicera version ${v}`,
  'cp.newVersion': 'Ny version…',
  'cp.published': 'Publicerat.',
  'cv.title': 'Skolans incheckningsresultat för elevkåren',
  'cv.notFound': 'Länken gäller inte längre. Be rektorn om en ny.',
  'cv.intro': (f) => `Anonyma resultat för hela skolan per hel vecka. En vecka med färre än ${f} svar döljs.`,
  'cv.weeks': 'Resultat per vecka',
  'cv.expires': (d) => `Länken fungerar till ${d}.`,
  'fs.title': 'Funktionsbrytare',
  'fs.hint': 'Varje funktion i arbetsflödena I och J kan stängas av för alla skolor på en gång. Databasen vägrar en avstängd funktion.',
  'fs.isOn': 'På',
  'fs.isOff': 'Av',
  'fs.on': (f) => `${f} har slagits på.`,
  'fs.off': (f) => `${f} har stängts av.`,
  'fs.teacher_session_flow': 'Tillägg för lärarens incheckning (prognos, förlängning)',
  'fs.teacher_predictions': 'Lärarens förutsägelser',
  'fs.private_notes': 'Privata anteckningar',
  'fs.you_said_we_did': 'Ni sa, vi gjorde',
  'fs.session_schedules': 'Återkommande incheckningar',
  'fs.head_insights': 'Rektorns resultatvy',
  'fs.welfare_report': 'Elevhälsorapport',
  'fs.compliance_pack': 'Dokumentpaket',
  'fs.playbook': 'Åtgärdsbank',
  'fs.action_log': 'Åtgärdslogg',
  'fs.counsellor_requests': 'Begäran om kuratorsbesök',
  'fs.student_council': 'Elevkårens länk',
  'fs.extra_languages': 'Extra språk för elever',
  'fs.swipe_checkin': 'Kortstapel-incheckning (kortstapel, hjälpremsa, ”Något annat”-alternativ)',
  'so.premium': 'Alternativ och gränser',
  'so.premiumHint': 'Tilläggsalternativ faktureras separat. Ändringar loggas och arkiveras.',
  'so.ent.custom_playbook': 'Egen åtgärdsbank (tillägg)',
  'so.ent.insights_premium': 'Utökade resultat (tillägg)',
  'so.sessionMax': 'Längsta incheckning (minuter)',
  'so.langs': 'Extra språk för elever',
  'so.langsHint': 'Eleverna kan välja dessa i incheckningen. Översättningarna är utkast. Skolan skriver sitt eget stödmeddelande på varje språk.',
  'so.netIn': (l) => `Stödmeddelande på ${l}`,
  'so.netFallback': 'Utan meddelande på ett språk ser eleverna skolans finska och engelska meddelanden.',
  'so.docs': 'Dokument som skolan undertecknar',
  'so.docsHint': 'Registrera en underskrift från det undertecknade exemplaret. Den sparas i arkivet.',
  'so.record': 'Registrera underskrift',
  'so.signedOn': 'Undertecknat den',
  'so.signatory': 'Undertecknat av',
  'err.bad_period': 'Välj en termin, läsåret eller minst 4 veckor.',
  'err.not_entitled': 'Din skola har inte aktiverat detta tilläggsalternativ.',
  'err.unknown_request': 'Det här väntar inte längre. Ladda om sidan.',
};
